import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner, FaTrash, FaPrint } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext"; // atau path kamu
import Select from "react-select";
import dayjs from 'dayjs';


// Interfaces
interface InboundOrderForm {
  id?: string; // ✅ Tambahkan baris ini
  inbound_order_no: string;
  po_number: string;
  vendor_id: string;
  warehouse_id: string;
  status?: string;
  created_by?: string;
  created_at?: string;
}

interface InboundOrderDetail {
  id?: string;
  productId: string;
  qtyExpected: number;
  qtyReceived?: number;
  expiryDate?: string;
  batchNumber?: string;
  qcStatus?: string;
}

interface Vendor {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  defaultLocationId?: string;
}

interface Location {
  id: string;
  name: string;
}

const InboundOrdersForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const { user } = useAuth();
  const userRole = user?.role || "user"; // fallback role
  const {logout} = useAuth();
  

  const [formData, setFormData] = useState<InboundOrderForm>({
    inbound_order_no: "",
    po_number: "",
    vendor_id: "",
    warehouse_id: "",
  });

  const [orderDetails, setOrderDetails] = useState<InboundOrderDetail[]>([
    {
      productId: "",
      qtyExpected: 0,
      qtyReceived: 0,
      expiryDate: "",
      batchNumber: "",
      qcStatus: "",
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const [vendorRes, whRes, prodRes, locRes] = await Promise.all([
        axios.get(`${API}/vendors`, getAuthHeader()),
        axios.get(`${API}/warehouses`, getAuthHeader()),
        axios.get(`${API}/products`, getAuthHeader()),
		axios.get(`${API}/locations`, getAuthHeader()), // 👈 tambahan ini
      ]);

      const extractData = (res: any) =>
        Array.isArray(res.data) ? res.data : res.data.data || [];

      setVendors(extractData(vendorRes));
      setWarehouses(extractData(whRes));
      setProducts(extractData(prodRes));
	  setLocations(extractData(locRes));
	  
      if (isEdit) {
		  const res = await axios.get(`${API}/inbound-orders/${id}`, getAuthHeader());
		  const data = res.data;
		
		  //console.log("Raw inbound order data:", data); // 👈 Log utama
		  //console.log("Raw details:", data.details);    // 👈 Fokus utama
		  
		  setFormData({
		    id: data.id, // ✅ Tambahkan ini
			inbound_order_no: data.inboundOrderNo || "",
			po_number: data.poNumber || "",
			vendor_id: data.vendor?.id || "",
			warehouse_id: data.warehouse?.id || "",
			status: data.status || "",
			created_by: data.createdByUser?.name || data.created_by || "",
			created_at: data.createdAt || "",
		  });

		  // Set order details AFTER products are loaded
		  setTimeout(() => {
			  setOrderDetails(
				Array.isArray(data.details)
				  ? data.details.map((d: any) => ({
					  id: d.id,
					  productId: d.product?.id || '',          // ✅ pakai d.product.id
					  qtyExpected: d.qtyExpected ?? 0,         // ✅ perhatikan casing
					  qtyReceived: d.qtyReceived ?? 0,
					  expiryDate: d.expiryDate
					  ? dayjs(d.expiryDate, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
					  : "",
					  batchNumber: d.batchNumber || '',
					  qcStatus: d.qcStatus || '',
					}))
				  : []
			  );
			}, 100);

		}

    } catch (error:any) {
      //console.error("Failed to fetch inbound orders", error);
	  if (error.response?.status === 401) {
		  logout();
		  //localStorage.removeItem("token"); // opsional: hapus token biar nggak infinite loop
		  //localStorage.removeItem("user");
		  navigate("/login"); // redirect ke login
		}
    } finally {
      setLoading(false);
    }
	
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (
    index: number,
    field: keyof InboundOrderDetail,
    value: any
  ) => {
    const updated = [...orderDetails];
    (updated[index] as any)[field] = field.includes("qty") ? Number(value) : value;
    setOrderDetails(updated);
  };

  const addDetailRow = () => {
    setOrderDetails([
      ...orderDetails,
      {
        productId: "",
        qtyExpected: 0,
        qtyReceived: 0,
        expiryDate: "",
        batchNumber: "",
        qcStatus: "",
      },
    ]);
  };

  const removeDetailRow = (index: number) => {
    if (orderDetails.length === 1) return;
    setOrderDetails(orderDetails.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    /*if (!formData.inbound_order_no) {
      alert("Order Number is required.");
      return;
    }*/

    const validDetails = orderDetails.filter(
      (d) => d.productId && d.qtyExpected > 0
    );

    if (validDetails.length === 0) {
      alert("Please add at least one valid product with quantity.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    try {
      const payload = {
		  //inboundOrderNo: formData.inbound_order_no,
		  poNumber: formData.po_number,
		  vendorId: formData.vendor_id,
		  warehouseId: formData.warehouse_id,
		  status: formData.status,
		  tenantId: user.tenantId,
		  createdByUserId: user.id,
		  details: validDetails.map((d) => ({
			id: d.id,
			productId: d.productId,
			qtyExpected: d.qtyExpected,
			qtyReceived: d.qtyReceived || 0,
			batchNumber: d.batchNumber,
			expiryDate: d.expiryDate
					  ? dayjs(d.expiryDate, ["DD/MM/YYYY", "YYYY-MM-DD"]).toISOString()
					  : undefined,

			qcStatus: d.qcStatus,
		  })),
		};


      if (isEdit) {
	    //console.log("Payload submitted:", payload);
        await axios.put(`${API}/inbound-orders/${id}`, payload, getAuthHeader());
        alert("Inbound Order updated!");
      } else {
		//console.log("Payload being sent to API:", payload);
        await axios.post(`${API}/inbound-orders`, payload, getAuthHeader());
        alert("Inbound Order created!");
      }

      navigate("/inbound-orders");
    } catch (error) {
      //console.error("Failed to save inbound order:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitStatus = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/inbound-orders/${formData.id}/submit`, {}, getAuthHeader());
      navigate("/inbound-orders");
	alert("Inbound Order Submitted!");
	setLoading(false);
  };

  const handleApprove = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/inbound-orders/${formData.id}/approve`, {}, getAuthHeader());
      navigate("/inbound-orders");
	alert("Inbound Order Approved!");
	setLoading(false);
  };

  const handleReject = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/inbound-orders/${formData.id}/reject`, {}, getAuthHeader());
      navigate("/inbound-orders");
	alert("Inbound Order Rejected!");
	setLoading(false);

  };
  
  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this data?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/inbound-orders/${id}`, getAuthHeader());
      alert("Inbound Order deleted");
      navigate("/inbound-orders");
    } catch (err) {
      //console.error("Delete error:", err);
      alert("Failed to delete Inbound Order");
    }
  };
  
  const handlePrint = async (): Promise<void> => {
	  const printWindow = window.open("", "_blank");

	  if (!printWindow) {
		alert("Popup blocked! Izinkan pop-up untuk mencetak.");
		return;
	  }

	  const formatDate = (value?: string) =>
		value ? dayjs(value).format("DD/MM/YYYY") : "-";

	  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

	  const locationMap = Object.fromEntries(locations.map((l) => [l.id, l.name]));

	  // 🔁 Fetch default location name per product
	  const defaultLocationMap: Record<string, string> = {};
	  for (const item of orderDetails) {
		try {
		  //const res = await axios.get(`${API}/inbound-orders/${id}`, getAuthHeader());
		  //const res = await axios.get(`${API}/products/${id}`, getAuthHeader());
		  const res = await axios.get(`${API}/products/${item.productId}`, getAuthHeader());
		  //console.log(res);
		  const product = res.data;
		  defaultLocationMap[item.productId] = product?.location?.name || "-";
		} catch (error) {
		  //console.error("Gagal fetch product", item.productId, error);
		  defaultLocationMap[item.productId] = "-";
		}
	  }
	  const html = `
		<html>
		  <head>
			<title>Putaway Form</title>
			<style>
			  body {
				font-family: Arial, sans-serif;
				font-size: 10px;
				padding: 12px;
			  }
			  .text-center { text-align: center; }
			  .border-line { border-top: 1px dashed #000; margin: 6px 0; }
			  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
			  th, td {
				border: 1px solid #000;
				padding: 4px;
				text-align: left;
				font-size: 9px;
			  }
			  th {
				background-color: #f0f0f0;
			  }
			  input.actual-location {
				width: 100%;
				border: 1px dashed #aaa;
				padding: 2px;
				font-size: 9px;
			  }
			  .signature-section {
				margin-top: 32px;
				display: flex;
				justify-content: space-between;
			  }
			  .signature-box {
				width: 40%;
				text-align: center;
				font-size: 10px;
			  }
			</style>
		  </head>
		  <body>
			<div class="text-center">
			  <h3>Putaway Form</h3>
			</div>
			<table>
			  <tr>
				<td><strong>Inbound Ord No:</strong> ${formData.inbound_order_no}</td>
				<td><strong>PO No:</strong> ${formData.po_number || "-"}</td>
			  </tr>
			  <tr>
				<td><strong>Warehouse:</strong> ${warehouses.find(w => w.id === formData.warehouse_id)?.name || "-"}</td>
				<td><strong>Vendor:</strong> ${vendors.find(v => v.id === formData.vendor_id)?.name || "-"}</td>
			  </tr>
			  <tr>
				<td><strong>Date:</strong> ${formatDate(formData.created_at)}</td>
				<td><strong>Operator:</strong> _______________________</td>
			  </tr>
			</table>

			<table>
			  <thead>
				<tr>
				  <th style="text-align:center;">No</th>
				  <th style="text-align:center;">Product</th>
				  <th style="text-align:center;">Qty Expected</th>
				  <th style="text-align:center;">Qty Received</th>
				  <th style="text-align:center;">Batch</th>
				  <th style="text-align:center;">Expiry</th>
				  <th style="text-align:center;">QC Status</th>
				  <th style="text-align:center;">Default Location</th>
				  <th style="text-align:center;">Actual Location</th>
				</tr>
			  </thead>
			  <tbody>
				${orderDetails
				  .map((item, i) => {
					const product = productMap[item.productId];
					const defaultLocation = defaultLocationMap[item.productId] || "-";

					return `
					  <tr>
						<td style="text-align:center;">${i + 1}</td>
						<td>${product?.name || "-"}</td>
						<td style="text-align:right;">${item.qtyExpected}</td>
						<td style="text-align:right;">${item.qtyReceived || ""}</td>
						<td>${item.batchNumber || ""}</td>
						<td>${formatDate(item.expiryDate)}</td>
						<td>${item.qcStatus || ""}</td>
						<td>${defaultLocation}</td>
						<td>&nbsp;</td>
					  </tr>`;
				  })
				  .join("")}
			  </tbody>
			</table>

			<div class="signature-section">
			  <div class="signature-box">
				<p>Prepared by:</p>
				<br/><br/><br/>
				<p>________________________</p>
				<p>Name & Sign</p>
			  </div>
			  <div class="signature-box">
				<p>Verified by:</p>
				<br/><br/><br/>
				<p>________________________</p>
				<p>Name & Sign</p>
			  </div>
			</div>

			<script>
			  window.onload = function() {
				window.focus();
				window.print();
				setTimeout(function() { window.close(); }, 500);
			  };
			</script>
		  </body>
		</html>
	  `;

	  printWindow.document.open();
	  printWindow.document.write(html);
	  printWindow.document.close();
	};


    
  const isDisabled = formData.status === "Approved" || formData.status === "Rejected" || formData.status === "Submitted";
  const isDelete = formData.status === "Draft"

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? "Edit Inbound Order" : "Add Inbound Order"}
          </h2>
          <div className="flex gap-2">
		    {formData.status === "Draft" && (
			  <button
				onClick={handleSubmitStatus}
				type="button"
				className="bg-yellow-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
			  >
				Submit
			  </button>
			)}
			
			{formData.status === "Approved" && (
			  <button
				type="button"
				onClick={handlePrint}
				className="bg-indigo-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
			  >
				<FaPrint /> Print Putaway Form
			  </button>
			)}

			{formData.status === "Submitted" &&  (
			  <>
				<button
				  onClick={handleApprove}
				  type="button"
				  className="bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
				>
				  Approve
				</button>
				<button
				  onClick={handleReject}
				  type="button"
				  className="bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
				>
				  Reject
				</button>
			  </>
			)}
			{formData.status !== "Approved" && formData.status !== "Rejected" && formData.status !== "Submitted" && (
            <button
              type="submit"
              form="inbound-form"
              className="bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />} Save
            </button>
			)}
            <button
              onClick={() => navigate("/inbound-orders")}
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded flex items-center gap-1 text-[13px]"
            >
              <FaTimes /> Cancel
            </button>
			{formData.status === "Draft" && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-2 text-[13px] hover:bg-red-700 disabled:opacity-50"
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="inbound-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">Inbound Order No</label>
                <input
                  type="text"
                  name="inbound_order_no"
                  value={formData.inbound_order_no}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  required
				  disabled
                />
            </div>
			<div className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">Reference No *</label>
                <input
                  type="text"
                  name="po_number"
                  value={formData.po_number}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  required
				  disabled={isDisabled}
                />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Warehouse *</label>
              <select
                name="warehouse_id"
                value={formData.warehouse_id}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
				disabled={isDisabled}
				required
              >
                <option value="">-- Select Warehouse --</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Vendor *</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
				disabled={isDisabled}
				required
              >
                <option value="">-- Select Vendor --</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>

            {[
              { label: "Status", value: formData.status },
              { label: "Created By", value: formData.created_by },
              {
                label: "Created At",
                value: formData.created_at
                  ? dayjs(formData.created_at).format("DD/MM/YYYY HH:mm")
                  : "",
              },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">{item.label}</label>
                <input
                  type="text"
                  value={item.value || ""}
                  disabled
                  className="flex-1 border rounded px-2 py-1 text-[13px] bg-gray-100"
                />
              </div>
            ))}
          </div>

          <div className="mt-6">
            <h3 className="font-semibold mb-2">Inbound Order Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px] border">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="border px-2 py-1 text-center">#</th>
                    <th className="border px-2 py-1 w-60 text-center">Product</th>
                    <th className="border px-2 py-1 text-center">Qty Expected</th>
                    <th className="border px-2 py-1 text-center">Qty Received</th>
                    <th className="border px-2 py-1 text-center">Batch</th>
                    <th className="border px-2 py-1 text-center">Expiry Date</th>
                    <th className="border px-2 py-1 w-20 text-center">QC Status</th>
                    <th className="border px-2 py-1 w-10 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.map((detail, index) => (
                    <tr key={index}>
                      <td className="border px-2 py-1 text-center">{index + 1}</td>
                      <td className="border px-2 py-1 ">
                        <Select
						  className="text-[13px]"
						  options={products.map((prod) => ({
							value: prod.id,
							label: prod.name,
						  }))}
						  value={products
							.map((prod) => ({
							  value: prod.id,
							  label: prod.name,
							}))
							.find((opt) => opt.value === detail.productId)}
						  onChange={(selected) =>
							handleDetailChange(index, "productId", selected?.value || "")
						  }
						  isDisabled={isDisabled}
						  menuPortalTarget={document.body}
						  menuPosition="fixed"
						  styles={{
							menuPortal: (base) => ({ ...base, zIndex: 9999 }),
							control: (base) => ({ ...base, fontSize: "13px" }),   // ukuran font di input
							option: (base) => ({ ...base, fontSize: "13px" }),    // ukuran font di dropdown
							singleValue: (base) => ({ ...base, fontSize: "13px" }), // ukuran font saat item terpilih
						  }}
						/>
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          className="w-full border rounded px-2 py-1 text-right"
                          value={detail.qtyExpected}
                          onChange={(e) =>
                            handleDetailChange(index, "qtyExpected", e.target.value)
                          }
						  disabled={isDisabled}
                        />
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          className="w-full border rounded px-2 py-1 text-right"
                          value={detail.qtyReceived || 0}
                          onChange={(e) =>
                            handleDetailChange(index, "qtyReceived", e.target.value)
                          }
						  disabled={isDisabled}
                        />
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="text"
                          className="w-full border rounded px-2 py-1"
                          value={detail.batchNumber || ""}
                          onChange={(e) =>
                            handleDetailChange(index, "batchNumber", e.target.value)
                          }
						  disabled={isDisabled}
                        />
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="date"
                          className="w-full border rounded px-2 py-1"
                          value={detail.expiryDate || ""}
                          onChange={(e) =>
                            handleDetailChange(index, "expiryDate", e.target.value)
                          }
						  disabled={isDisabled}
                        />
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="text"
                          className="w-full border rounded px-2 py-1"
                          value={detail.qcStatus || ""}
                          onChange={(e) =>
                            handleDetailChange(index, "qcStatus", e.target.value)
                          }
						  disabled={isDisabled}
                        />
                      </td>
                      {!isDisabled && (<td className="border px-2 py-1 text-center">
						<button
						type="button"
						className="text-red-600 hover:text-red-800 mr-2"
                        title="Delete Item"
                        onClick={() => removeDetailRow(index)}
                        disabled={orderDetails.length === 1}
						
                      >
                        <FaTrash />
                      </button>
                      </td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isDisabled && (<button
              type="button"
              onClick={addDetailRow}
              className="mt-2 bg-green-600 text-white px-3 py-1 rounded text-[13px]"
            >
              + Add Product
            </button>)}
          </div>
        </form>
      </div>
    </div>
  );
};

export default InboundOrdersForm;
